import { ActionItemType, CanvasType } from 'Core-utils/types';
import { Typography } from 'Components/atoms';
import { DynamicDropdown } from 'Components/molecules';
import { DynamicDropdownProps } from 'Components/molecules/DynamicDropdown/DynamicDropdown';
import { Form } from 'react-bootstrap';
import classNames from 'classnames';
import { DropdownComponent } from 'Components/molecules';
import { generateDropdownValues } from 'Core-utils/helpers/helpers';
import { HiInformationCircle } from '@react-icons/all-files/hi2/HiInformationCircle';
import {
    CONFIGURE_MORE_ACTIONS,
    DONT_CHANGE,
    generateAllItems,
    generateAllStatus,
} from 'Core-utils/constants/constants';
import { ACTIONS_WORKFLOWS_ROUTE } from 'Core-utils/routes';
import './styles.scss';

export const MESSAGE_TONE_ID = 1;
export const SCHEDULE_RESOLUTION_ID = 2;
export const RESOLUTION_DEADLINE_ID = 3;
export const REMINDER_ID = 4;
export const HELP_ID = 5;
export const RESOLUTION_EXPIRY_ACTION_ID = 6;
export const RESOLVE_NOW_ID = 7;
export const TRIAGE_ID = 8;
export const CUSTOM_MESSAGE_ID = 9;
export const ADDITIONAL_CONTEXT_ID = 10;
export const INCLUDE_MANAGER_ID = 11;
export const INSTRUCTION_ID = 12;
export const MARK_COMPLETE_ID = 13;
export const REWARDS_ID = 14;

type SingleDropdownSelection<T> = Pick<DynamicDropdownProps<T>, 'options' | 'optionWithLinks'> & {
    selectedOptions?: string;
    onOptionSelected: (option: string, fieldName: string, id: number, selectedId?: string) => void;
    fieldName: string;
    id: number;
};

type MultiDropdownSelection<T> = Pick<DynamicDropdownProps<T>, 'options'> & {
    selectedOptions?: string[];
    onOptionSelected: (option: string[], fieldName: string, id: number) => void;
    fieldName: string;
    id: number;
};

export type RewardsProps = {
    value?: string;
    id: number;
    duration?: string;
    time?: string;
    unit?: string;
    onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
} & Pick<SingleDropdownSelection<string>, 'onOptionSelected'>;

export type MessageToneProps = Pick<SingleDropdownSelection<string>, 'onOptionSelected'> & {
    id: number;
    nature?: string;
};

export type ResolutionDeadlineProps = Pick<SingleDropdownSelection<string>, 'onOptionSelected'> & {
    id: number;
    deadlineTime?: string;
    deadlineUnit?: string;
};

type TextAreaWrapperProps = {
    value?: string;
    onChange: (option: string, fieldName: string, id: number) => void;
    fieldName: string;
    id: number;
    onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
    variant?: 'custom' | 'context';
    placeHolder?: string;
};

type ToggleWrapperProps = {
    id: number;
    checked?: boolean;
    onChange?: () => void;
};

type CustomMessageProps = Pick<SingleDropdownSelection<string>, 'onOptionSelected'> & {
    id: number;
    template?: string;
    checked?: boolean;
    onChange?: () => void;
    error?: boolean;
};

type HelpProps = Pick<MultiDropdownSelection<string[]>, 'onOptionSelected'> & {
    id: number;
    options?: string[];
};

export type ReminderProps = Pick<SingleDropdownSelection<string>, 'onOptionSelected'> & {
    id: number;
    reminderTime?: string;
    reminderUnit?: string;
};

type ResolutionExpiryActionProps = Pick<SingleDropdownSelection<string>, 'onOptionSelected'> & {
    id: number;
    actionId?: string;
    actions?: ActionItemType[];
    findingStatus?: string;
    error?: boolean;
};

type ResolveNowProps = Pick<SingleDropdownSelection<string>, 'onOptionSelected'> & {
    id: number;
    actionId?: string;
    actions?: ActionItemType[];
    findingStatus?: string;
    error?: boolean;
};

type TriageProps = Pick<SingleDropdownSelection<string>, 'onOptionSelected'> & {
    id: number;
    yesActionId?: string;
    noActionId?: string;
    actions: ActionItemType[];
    yesActionStatus?: string;
    noActionStatus?: string;
    triageMessage?: string;
    error?: boolean;
};

type AdditionalContextProps = Pick<SingleDropdownSelection<string>, 'onOptionSelected'> & {
    id: number;
    data?: string;
    error?: boolean;
};

type ScheduleResolutionProps = Pick<SingleDropdownSelection<string>, 'onOptionSelected'> & {
    id: number;
    actions: ActionItemType[];
    actionId?: string;
    findingStatus?: string;
};

const MultiSelectDropdownWrapper = ({
    options,
    selectedOptions,
    onOptionSelected,
    fieldName,
    id,
}: MultiDropdownSelection<string>) => {
    const handleCheckbox = (option: string[] | undefined) => {
        if (option !== undefined) {
            onOptionSelected(option, fieldName, id);
        }
    };
    const normalizedSelectedOptions = selectedOptions ?? [];

    return (
        <DynamicDropdown
            variant="multiSelect"
            selectedOptions={normalizedSelectedOptions}
            onOptionSelected={handleCheckbox}
            options={options}
            selectedOptionsClassName="custom"
        />
    );
};

const SingleSelectDropdownWrapper = ({
    options,
    selectedOptions,
    onOptionSelected,
    fieldName,
    id,
}: SingleDropdownSelection<string>) => {
    const handleCheckbox = (option: string) => {
        onOptionSelected(option, fieldName, id);
    };
    return (
        <DynamicDropdown
            variant="default"
            selectedOptions={selectedOptions ?? ''}
            onOptionSelected={handleCheckbox}
            options={options}
        />
    );
};

const ActionsDropdownWrapper = ({
    options,
    selectedOptions,
    onOptionSelected,
    fieldName,
    id,
    displayActionsRoute = true,
}: any) => {
    const handleCheckbox = (selectedOption: string | null, event: any) => {
        onOptionSelected(selectedOption, fieldName, id, event.currentTarget.id);
    };
    const statusFieldNames = ['findingStatus', 'yesActionStatus', 'noActionStatus'];
    const updatedOptions = displayActionsRoute
        ? [
              ...options,
              {
                  id: CONFIGURE_MORE_ACTIONS,
                  value: CONFIGURE_MORE_ACTIONS,
                  icon: <HiInformationCircle className="text-primary-500 side-panel__icon" />,
                  link: ACTIONS_WORKFLOWS_ROUTE,
              },
          ]
        : options;
    const option = options.find((item: any) => item?.id === selectedOptions);
    let value = statusFieldNames.includes(fieldName) ? selectedOptions : option?.value;
    if (option?.displayValue) {
        value = option?.displayValue;
    }
    return (
        <DropdownComponent
            value={value}
            placeholder="Select"
            dropDownItems={updatedOptions}
            onChange={handleCheckbox}
            selectedItemIcon={options.find((item: any) => item?.id === selectedOptions)?.icon}
            maxWidth="5rem"
        />
    );
};

const ToggleWrapper = ({ id, onChange, checked }: ToggleWrapperProps) => {
    return <Form.Check type="switch" checked={checked} onChange={onChange} id={id.toString()} />;
};

const TextAreaWrapper = ({ value, onChange, id, fieldName, variant, placeHolder }: TextAreaWrapperProps) => {
    const handleChange = (event: any) => {
        onChange(event.target.value, fieldName, id);
    };
    return (
        <Form.Control
            as="textarea"
            value={value}
            placeholder={placeHolder}
            onChange={handleChange}
            className={classNames(
                'shadow-none subtitle7 w-100 bg-transparent text-text-high-emphasis overflow-auto custom-scrollbar side-panel__text-area p-3',
                { 'border-0': variant === 'custom', 'side-panel__context-area': variant === 'context' },
            )}
        />
    );
};

const DbValueTextAreaWrapper = ({ value, onChange, id, fieldName, onKeyDown }: TextAreaWrapperProps) => {
    const handleChange = (event: any) => {
        onChange(event.target.value, fieldName, id);
    };
    return (
        <Form.Control
            id={id.toString()}
            type="text"
            value={value}
            onChange={handleChange}
            onKeyDown={onKeyDown}
            className="shadow-none bg-transparent side-panel__db-text-area text-text-high-emphasis"
        />
    );
};

const Rewards = ({ value, id, duration, unit, time, onOptionSelected, onKeyDown }: RewardsProps) => (
    <Typography
        variant="subtitle7"
        style={{ marginBottom: '0%', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center' }}
    >
        On completing the action&nbsp;&nbsp;
        <SingleSelectDropdownWrapper
            options={['within', 'after']}
            selectedOptions={duration}
            onOptionSelected={onOptionSelected}
            fieldName="duration"
            id={id}
        />
        &nbsp;
        <SingleSelectDropdownWrapper
            options={Array.from({ length: 31 }, (_, index) => (index + 1).toString())}
            selectedOptions={time}
            onOptionSelected={onOptionSelected}
            fieldName="time"
            id={id}
        />
        &nbsp;
        <SingleSelectDropdownWrapper
            options={['hours', 'days', 'weeks']}
            selectedOptions={unit}
            onOptionSelected={onOptionSelected}
            fieldName="unit"
            id={id}
        />
        &nbsp; users will be rewarded&nbsp;&nbsp;
        <DbValueTextAreaWrapper
            value={value}
            onChange={onOptionSelected}
            id={id}
            fieldName="value"
            onKeyDown={onKeyDown}
        />
        &nbsp; dBs
    </Typography>
);

const MessageTone = ({ onOptionSelected, id, nature }: MessageToneProps) => {
    return (
        <div className="d-flex gap-2">
            <Typography
                variant="subtitle7"
                style={{ marginBottom: '0%', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center' }}
            >
                Send all messages with a
            </Typography>
            <SingleSelectDropdownWrapper
                options={['Humorous', 'Friendly', 'Serious']}
                fieldName="nature"
                onOptionSelected={onOptionSelected}
                id={id}
                selectedOptions={nature}
            />
            <Typography variant="subtitle7" style={{ marginBottom: '0%', display: 'flex', alignItems: 'center' }}>
                tone.
            </Typography>
        </div>
    );
};

const ScheduleResolution = ({ onOptionSelected, id, actionId, actions, findingStatus }: ScheduleResolutionProps) => {
    return (
        <div className="d-flex gap-2">
            <Typography
                variant="subtitle7"
                style={{ marginBottom: '0%', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center' }}
            >
                Require users to schedule a resolution time and take
            </Typography>
            <div className="side-panel__action">
                <ActionsDropdownWrapper
                    options={generateAllItems(actions)}
                    fieldName="actionId"
                    id={id}
                    selectedOptions={actionId}
                    onOptionSelected={onOptionSelected}
                />
            </div>
            <Typography
                variant="subtitle7"
                style={{ marginBottom: '0%', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center' }}
            >
                and change finding status to
            </Typography>
            <div className="side-panel__action">
                <ActionsDropdownWrapper
                    options={generateAllStatus()}
                    fieldName="findingStatus"
                    id={id}
                    selectedOptions={findingStatus ?? DONT_CHANGE}
                    onOptionSelected={onOptionSelected}
                    displayActionsRoute={false}
                />
            </div>
        </div>
    );
};

const INCLUDE_MANAGER = () => <>Copies direct manager in all engagements when available.</>;

const INSTRUCTIONS = () => <>Provide detailed instructions button.</>;

const MARK_COMPLETE = () => <>Provide mark complete button.</>;

const ResolutionDeadline = ({ id, deadlineTime, deadlineUnit, onOptionSelected }: ResolutionDeadlineProps) => {
    return (
        <div className="d-flex gap-2">
            <Typography
                variant="subtitle7"
                style={{ marginBottom: '0%', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center' }}
            >
                This finding should be resolved within
            </Typography>
            <SingleSelectDropdownWrapper
                options={generateDropdownValues()}
                fieldName="deadlineTime"
                onOptionSelected={onOptionSelected}
                selectedOptions={deadlineTime}
                id={id}
            />
            <SingleSelectDropdownWrapper
                options={['hours', 'days', 'weeks']}
                fieldName="deadlineUnit"
                onOptionSelected={onOptionSelected}
                selectedOptions={deadlineUnit}
                id={id}
            />
        </div>
    );
};

const Reminder = ({ onOptionSelected, id, reminderTime, reminderUnit }: ReminderProps) => {
    return (
        <div className="d-flex gap-2">
            <Typography
                variant="subtitle7"
                style={{ marginBottom: '0%', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center' }}
            >
                Remind users every
            </Typography>
            <SingleSelectDropdownWrapper
                options={generateDropdownValues()}
                selectedOptions={reminderTime}
                id={id}
                fieldName="reminderTime"
                onOptionSelected={onOptionSelected}
            />
            <SingleSelectDropdownWrapper
                options={['hours', 'days', 'weeks']}
                selectedOptions={reminderUnit}
                id={id}
                fieldName="reminderUnit"
                onOptionSelected={onOptionSelected}
            />
        </div>
    );
};

const Help = ({ onOptionSelected, id, options }: HelpProps) => {
    return (
        <div className="d-flex gap-2">
            <Typography
                variant="subtitle7"
                style={{ marginBottom: '0%', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center' }}
            >
                Provide button for getting help via
            </Typography>
            <div className="side-panel__action">
                <MultiSelectDropdownWrapper
                    options={['Option1', 'Option2', 'Option3']}
                    id={id}
                    onOptionSelected={onOptionSelected}
                    fieldName="multiSelect"
                    selectedOptions={options}
                />
            </div>
        </div>
    );
};

const ResolutionExpiryAction = ({
    id,
    onOptionSelected,
    actionId,
    actions,
    findingStatus,
    error,
}: ResolutionExpiryActionProps) => {
    const actionItems = actions?.map((item) => ({
        id: item.id,
        icon: item.serviceKey?.logo,
        value: item.displayValue,
    }));
    return (
        <div className="d-flex flex-column gap-1">
            <div className="d-flex gap-2">
                <Typography
                    variant="subtitle7"
                    style={{ marginBottom: '0%', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center' }}
                >
                    When user fails to resolve by the deadline, take
                </Typography>
                <div className="side-panel__action">
                    <ActionsDropdownWrapper
                        options={actionItems}
                        fieldName="actionId"
                        id={id}
                        selectedOptions={actionId}
                        onOptionSelected={onOptionSelected}
                    />
                </div>
                <Typography
                    variant="subtitle7"
                    style={{ marginBottom: '0%', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center' }}
                >
                    and change finding status to
                </Typography>
                <div className="side-panel__action">
                    <ActionsDropdownWrapper
                        options={generateAllStatus()}
                        fieldName="findingStatus"
                        id={id}
                        selectedOptions={findingStatus ?? DONT_CHANGE}
                        onOptionSelected={onOptionSelected}
                        displayActionsRoute={false}
                    />
                </div>
            </div>
            {error && (
                <Typography variant="overline2" color="vizualization-red-200">
                    Please select a action.
                </Typography>
            )}
        </div>
    );
};

const ResolveNow = ({ id, actionId, onOptionSelected, actions, findingStatus, error }: ResolveNowProps) => {
    const actionItems = actions?.map((item) => ({
        id: item.id,
        icon: item.serviceKey?.logo,
        value: item.displayValue,
    }));
    return (
        <div className="d-flex flex-column gap-1">
            <div className="d-flex gap-2">
                <Typography
                    variant="subtitle7"
                    style={{
                        whiteSpace: 'nowrap',
                        display: 'flex',
                        marginBottom: '0%',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                    }}
                >
                    Provide users with “Resolve Now” button to launch predefined &nbsp;&nbsp;
                    <span className="side-panel__action">
                        <ActionsDropdownWrapper
                            options={actionItems}
                            id={id}
                            fieldName="actionId"
                            selectedOptions={actionId}
                            onOptionSelected={onOptionSelected}
                        />
                    </span>
                    &nbsp;&nbsp;automation instantly.&nbsp; The finding status should be changed to&nbsp;
                    <span className="side-panel__action">
                        <ActionsDropdownWrapper
                            options={generateAllStatus()}
                            id={id}
                            fieldName="findingStatus"
                            selectedOptions={findingStatus ?? DONT_CHANGE}
                            onOptionSelected={onOptionSelected}
                            displayActionsRoute={false}
                        />
                    </span>
                </Typography>
            </div>
            {error && (
                <Typography variant="overline2" color="vizualization-red-200">
                    Please select a action.
                </Typography>
            )}
        </div>
    );
};

const Triage = ({
    id,
    onOptionSelected,
    yesActionId,
    noActionId,
    actions,
    yesActionStatus,
    noActionStatus,
    triageMessage,
    error,
}: TriageProps) => {
    return (
        <div className="d-flex flex-column gap-2">
            <div className="d-flex flex-column">
                <Typography
                    variant="subtitle7"
                    style={{
                        whiteSpace: 'nowrap',
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        marginBottom: '0%',
                    }}
                >
                    If user answers YES, take &nbsp;
                    <div className="side-panel__action">
                        <ActionsDropdownWrapper
                            options={generateAllItems(actions)}
                            id={id}
                            fieldName="yesActionId"
                            selectedOptions={yesActionId}
                            onOptionSelected={onOptionSelected}
                        />
                    </div>
                    &nbsp;&nbsp;and change finding status to &nbsp;&nbsp;
                    <div className="side-panel__action">
                        <ActionsDropdownWrapper
                            options={generateAllStatus()}
                            id={id}
                            fieldName="yesActionStatus"
                            selectedOptions={yesActionStatus ?? DONT_CHANGE}
                            onOptionSelected={onOptionSelected}
                            displayActionsRoute={false}
                        />
                    </div>
                </Typography>
            </div>
            <div className="d-flex flex-column">
                <Typography
                    variant="subtitle7"
                    style={{
                        whiteSpace: 'nowrap',
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        marginBottom: '0%',
                    }}
                >
                    If user answers NO, take &nbsp;
                    <div className="side-panel__action">
                        <ActionsDropdownWrapper
                            options={generateAllItems(actions)}
                            id={id}
                            fieldName="noActionId"
                            selectedOptions={noActionId}
                            onOptionSelected={onOptionSelected}
                        />
                    </div>
                    &nbsp;&nbsp;and change finding status to &nbsp;&nbsp;
                    <div className="side-panel__action">
                        <ActionsDropdownWrapper
                            options={generateAllStatus()}
                            id={id}
                            fieldName="noActionStatus"
                            selectedOptions={noActionStatus ?? DONT_CHANGE}
                            onOptionSelected={onOptionSelected}
                            displayActionsRoute={false}
                        />
                    </div>
                </Typography>
            </div>
            <div className="d-flex flex-column gap-3 w-100 side-panel__custom-message">
                <TextAreaWrapper
                    onChange={onOptionSelected}
                    fieldName="triageMessage"
                    id={id}
                    value={triageMessage}
                    variant="custom"
                    placeHolder="Message"
                />
            </div>
            {error && (
                <Typography variant="overline2" color="vizualization-red-200">
                    Please Enter Message.
                </Typography>
            )}
        </div>
    );
};

const CustomMessage = ({ onOptionSelected, id, template, onChange, checked, error }: CustomMessageProps) => {
    return (
        <div className="d-flex flex-column w-100">
            <div className="d-flex flex-column gap-3 w-100 side-panel__custom-message">
                <TextAreaWrapper
                    onChange={onOptionSelected}
                    fieldName="template"
                    id={id}
                    value={template}
                    variant="custom"
                    placeHolder="Custom Message"
                />
                <div className="side-panel__hr" />
                <div className="d-flex gap-2 ps-3 pb-3">
                    <ToggleWrapper id={id} onChange={onChange} checked={checked} />
                    <Typography
                        variant="subtitle7"
                        style={{ marginBottom: '0%', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center' }}
                    >
                        Only include custom message
                    </Typography>
                </div>
            </div>
            {error && (
                <Typography variant="overline2" color="vizualization-red-200">
                    Please Enter Message.
                </Typography>
            )}
        </div>
    );
};

const AdditionalContext = ({ onOptionSelected, id, data, error }: AdditionalContextProps) => {
    return (
        <div className="d-flex flex-column gap-1 w-100">
            <TextAreaWrapper
                onChange={onOptionSelected}
                fieldName="data"
                id={id}
                value={data}
                variant="context"
                placeHolder="Additional Context"
            />
            {error && (
                <Typography variant="overline2" color="vizualization-red-200">
                    Please Enter Message.
                </Typography>
            )}
        </div>
    );
};

type MockDataUnion =
    | typeof Rewards
    | typeof MessageTone
    | typeof Reminder
    | typeof ScheduleResolution
    | typeof CustomMessage
    | typeof Triage
    | typeof ResolutionDeadline
    | typeof ResolutionExpiryAction
    | typeof Help
    | typeof ResolveNow
    | typeof AdditionalContext
    | typeof INSTRUCTIONS
    | typeof INCLUDE_MANAGER;

export type MockDataType = Omit<CanvasType, 'value' | 'dropdownKey'> & { value: MockDataUnion };

export const MOCK_DATA: MockDataType[] = [
    {
        id: MESSAGE_TONE_ID,
        name: 'Message tone',
        isDraggable: true,
        disabled: false,
        value: MessageTone,
    },
    {
        id: SCHEDULE_RESOLUTION_ID,
        name: 'Schedule Resolution',
        isDraggable: true,
        disabled: false,
        value: ScheduleResolution,
    },
    {
        id: RESOLUTION_DEADLINE_ID,
        name: 'Resolution Deadline',
        isDraggable: true,
        disabled: false,
        value: ResolutionDeadline,
    },
    {
        id: REMINDER_ID,
        name: 'Reminder',
        isDraggable: true,
        disabled: false,
        value: Reminder,
    },
    {
        id: HELP_ID,
        name: 'Help',
        isDraggable: true,
        disabled: false,
        value: Help,
    },
    {
        id: RESOLUTION_EXPIRY_ACTION_ID,
        name: 'Resolution Expiry Action',
        isDraggable: true,
        disabled: false,
        value: ResolutionExpiryAction,
    },
    {
        id: RESOLVE_NOW_ID,
        name: 'Resolve Now',
        isDraggable: true,
        disabled: false,
        value: ResolveNow,
    },
    {
        id: TRIAGE_ID,
        name: 'Triage (Yes/No)',
        isDraggable: true,
        disabled: false,
        value: Triage,
    },
    {
        id: CUSTOM_MESSAGE_ID,
        name: 'Custom Message',
        isDraggable: true,
        disabled: false,
        value: CustomMessage,
    },
    {
        id: ADDITIONAL_CONTEXT_ID,
        name: 'Additional Context',
        isDraggable: true,
        disabled: false,
        value: AdditionalContext,
    },
    {
        id: INCLUDE_MANAGER_ID,
        name: 'Include Manager',
        isDraggable: true,
        disabled: false,
        value: INCLUDE_MANAGER,
    },
    {
        id: INSTRUCTION_ID,
        name: 'Instructions',
        isDraggable: true,
        disabled: false,
        value: INSTRUCTIONS,
    },
    {
        id: MARK_COMPLETE_ID,
        name: 'Mark Complete',
        isDraggable: true,
        disabled: false,
        value: MARK_COMPLETE,
    },
    {
        id: REWARDS_ID,
        name: 'Rewards',
        isDraggable: true,
        disabled: false,
        value: Rewards,
    },
];
